import { useEffect, useState } from "react";
import { Search, X, Download } from "lucide-react";
import { toast } from "sonner";
import { api, adminFileUrl, formatApiError } from "@/lib/api";

const inputCls = "field-input";
const BOOKING_STATUSES = ["pending", "confirmed", "completed", "cancelled", "no-show"];
const PAYMENT_STATUSES = ["unpaid", "pending", "paid", "cancelled", "refunded"];

export default function AdminBookings() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [bookingStatus, setBookingStatus] = useState("");
  const [paymentStatus, setPaymentStatus] = useState("");
  const [active, setActive] = useState(null);
  const [typeFilter, setTypeFilter] = useState("");
  const [view, setView] = useState("bookings");
  const [requests, setRequests] = useState([]);

  const loadRequests = () => {
    api.get("/admin/scheduling-requests").then((r) => setRequests(r.data)).catch(() => {});
  };
  useEffect(() => { if (view === "requests") loadRequests(); }, [view]);

  const patchRequest = async (id, status) => {
    try {
      await api.patch(`/admin/scheduling-requests/${id}`, { status });
      toast.success("Updated");
      loadRequests();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const deleteRequest = async (id) => {
    try {
      await api.delete(`/admin/scheduling-requests/${id}`);
      toast.success("Deleted");
      loadRequests();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const load = () => {
    api.get("/admin/expert-bookings", { params: { search, booking_status: bookingStatus, payment_status: paymentStatus, consultation_type: typeFilter } })
      .then((r) => setItems(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [search, bookingStatus, paymentStatus, typeFilter]);

  const patch = async (id, payload) => {
    try {
      const { data } = await api.patch(`/admin/expert-bookings/${id}`, payload);
      toast.success("Updated");
      load();
      if (active?.id === id) setActive(data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-bookings">
      <div className="mb-4 flex gap-2" data-testid="admin-bookings-view-toggle">
        <button onClick={() => setView("bookings")} data-testid="admin-view-bookings"
          className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${view === "bookings" ? "bg-navy text-white" : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
          Expert Bookings
        </button>
        <button onClick={() => setView("requests")} data-testid="admin-view-scheduling-requests"
          className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${view === "requests" ? "bg-navy text-white" : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
          Scheduling Requests
        </button>
      </div>
      {view === "requests" ? (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
          <table className="w-full min-w-[1000px] text-sm" data-testid="scheduling-requests-table">
            <thead>
              <tr className="border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500">
                {["Request ID", "Expert", "Client", "Contact", "Type", "Topic", "Preferred times", "Received", "Status"].map((h) => (
                  <th key={h} className="px-4 py-3 text-start font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {requests.map((r) => (
                <tr key={r.id} className="border-b border-slate-50 hover:bg-slate-50/60">
                  <td className="px-4 py-3 font-mono text-xs font-bold text-navy">{r.request_id}</td>
                  <td className="px-4 py-3 text-slate-700">{r.expert_name}</td>
                  <td className="px-4 py-3 font-semibold text-navy">{r.client_name}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">
                    {r.email}
                    {r.phone && <span className="block">{r.phone}</span>}
                    {r.whatsapp && <span className="block">WA: {r.whatsapp}</span>}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{r.consultation_type}</td>
                  <td className="max-w-44 px-4 py-3 text-slate-600">{r.topic || "-"}</td>
                  <td className="max-w-44 px-4 py-3 text-slate-600">{r.preferred_times || "-"}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">{(r.created_at || "").slice(0, 10)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <select value={r.status} onChange={(e) => patchRequest(r.id, e.target.value)} data-testid={`scheduling-status-${r.id}`} className="rounded border border-slate-200 px-2 py-1 text-xs">
                        {["new", "contacted", "scheduled", "closed"].map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                      <button onClick={() => deleteRequest(r.id)} data-testid={`scheduling-delete-${r.id}`} aria-label="Delete request"
                        className="rounded-full p-1.5 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600">
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {requests.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">No scheduling requests yet.</td></tr>}
            </tbody>
          </table>
        </div>
      ) : (
      <>
      <div className="mb-5 flex flex-wrap gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="relative min-w-64 flex-1">
          <Search className="absolute start-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input className={`${inputCls} ps-9`} placeholder="Search client, expert or booking ID..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="admin-booking-search" />
        </div>
        <select className={inputCls} style={{ maxWidth: 180 }} value={bookingStatus} onChange={(e) => setBookingStatus(e.target.value)} data-testid="admin-booking-filter-status">
          <option value="">Booking: All</option>
          {BOOKING_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className={inputCls} style={{ maxWidth: 180 }} value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value)} data-testid="admin-booking-filter-payment">
          <option value="">Payment: All</option>
          {PAYMENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select className={inputCls} style={{ maxWidth: 180 }} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="admin-booking-filter-type">
          <option value="">Type: All</option>
          <option value="expert">Expert</option>
          <option value="research">Research</option>
        </select>
      </div>

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm">
        <table className="w-full min-w-[1050px] text-sm" data-testid="admin-bookings-table">
          <thead>
            <tr className="border-b border-slate-100 text-xs uppercase tracking-wider text-slate-500">
              {["Booking ID", "Expert", "Client", "Date & Time", "Duration", "Total", "Booking Status", "Payment", ""].map((h) => (
                <th key={h} className="px-4 py-3 text-start font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((b) => (
              <tr key={b.id} className="border-b border-slate-50 hover:bg-slate-50/60">
                <td className="px-4 py-3 font-mono text-xs font-bold text-navy">{b.booking_id}
                  <span className="block text-[10px] font-semibold uppercase text-slate-400">{b.consultation_type || "expert"}</span>
                </td>
                <td className="px-4 py-3 text-slate-700">{b.expert_name}</td>
                <td className="px-4 py-3 font-semibold text-navy">{b.client_name}</td>
                <td className="px-4 py-3 text-slate-600">{b.date} {b.time}<span className="block text-[10px] text-slate-400">{b.timezone}</span></td>
                <td className="px-4 py-3 text-slate-600">{b.duration} min</td>
                <td className="px-4 py-3 font-bold text-navy">USD {Number(b.total_amount).toLocaleString()}</td>
                <td className="px-4 py-3">
                  <select value={b.booking_status} onChange={(e) => patch(b.id, { booking_status: e.target.value })} data-testid={`booking-status-${b.id}`} className="rounded border border-slate-200 px-2 py-1 text-xs">
                    {BOOKING_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <select value={b.payment_status} onChange={(e) => patch(b.id, { payment_status: e.target.value })} data-testid={`booking-payment-${b.id}`} className="rounded border border-slate-200 px-2 py-1 text-xs">
                    {PAYMENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </td>
                <td className="px-4 py-3">
                  <button onClick={() => setActive(b)} data-testid={`booking-view-${b.id}`} className="text-xs font-bold text-gold-hover hover:underline">View</button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">No bookings yet.</td></tr>}
          </tbody>
        </table>
      </div>
      </>
      )}

      {active && (
        <div className="fixed inset-0 z-[100] overflow-y-auto bg-navy-950/70 p-4" onClick={() => setActive(null)} data-testid="booking-detail-modal">
          <div className="mx-auto my-8 max-w-2xl rounded-lg bg-white p-8 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-5 flex items-start justify-between">
              <div>
                <h2 className="font-display text-2xl font-semibold text-navy">{active.booking_id}</h2>
                <p className="mt-1 text-sm text-slate-500">{active.expert_name} · {active.date} at {active.time} ({active.timezone})</p>
              </div>
              <button onClick={() => setActive(null)} className="rounded-full bg-slate-100 p-2" aria-label="Close"><X className="h-5 w-5" /></button>
            </div>
            <div className="grid gap-x-8 gap-y-3 text-sm sm:grid-cols-2">
              {[
                ["Client", active.client_name], ["Email", active.email], ["Phone", active.phone],
                ["WhatsApp", active.whatsapp], ["Country", active.country],
                ["Organization", active.organization], ["Job title", active.job_title],
                ["Duration", `${active.duration} min`], ["Hourly rate", `USD ${active.hourly_rate}`],
                ["Total", `USD ${active.total_amount}`], ["Payment method", active.payment_method],
                ["Payment reference", active.payment_reference],
              ].map(([k, v]) => v ? (
                <div key={k}><span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">{k}</span><span className="break-words text-slate-700">{v}</span></div>
              ) : null)}
            </div>
            {active.topic && <div className="mt-4"><span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">Topic</span><p className="mt-1 text-sm text-slate-700">{active.topic}</p></div>}
            {active.notes && <div className="mt-4"><span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">Notes</span><p className="mt-1 whitespace-pre-line text-sm text-slate-700">{active.notes}</p></div>}
            {active.payment_proof_path && (
              <a href={adminFileUrl(active.payment_proof_path)} data-testid="booking-proof-download"
                className="mt-5 inline-flex items-center gap-2 rounded-full bg-navy px-4 py-2 text-xs font-bold text-white hover:bg-navy-800">
                <Download className="h-3.5 w-3.5" /> {active.payment_proof_filename || "Payment proof"}
              </a>
            )}
            {active.files?.filter((f) => f.kind === "document").length > 0 && (
              <div className="mt-4">
                <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-400">Client documents</span>
                <div className="mt-1.5 flex flex-wrap gap-3">
                  {active.files.filter((f) => f.kind === "document").map((f, i) => (
                    <a key={i} href={adminFileUrl(f.path)} data-testid={`booking-doc-${i}`} className="inline-flex items-center gap-1.5 text-xs font-semibold text-gold-hover hover:underline">
                      <Download className="h-3.5 w-3.5" /> {f.filename}
                    </a>
                  ))}
                </div>
              </div>
            )}
            <div className="mt-5">
              <label className="text-xs font-semibold text-slate-500">Meeting link (visible to client &amp; expert after confirmation)</label>
              <div className="mt-1.5 flex gap-2">
                <input className={inputCls} placeholder="https://meet..." defaultValue={active.meeting_link || ""} id="admin-meeting-link" data-testid="admin-meeting-link-input" />
                <button
                  data-testid="admin-meeting-link-save"
                  onClick={() => patch(active.id, { meeting_link: document.getElementById("admin-meeting-link").value })}
                  className="whitespace-nowrap rounded-full bg-navy px-4 py-2 text-xs font-bold text-white hover:bg-navy-800"
                >
                  Save Link
                </button>
              </div>
            </div>
            <div className="mt-6 flex flex-wrap gap-4 border-t border-slate-100 pt-5">
              <label className="text-xs font-semibold text-slate-500">
                Booking status
                <select className={`${inputCls} mt-1`} value={active.booking_status} onChange={(e) => patch(active.id, { booking_status: e.target.value })} data-testid="booking-modal-status">
                  {BOOKING_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
              <label className="text-xs font-semibold text-slate-500">
                Payment status
                <select className={`${inputCls} mt-1`} value={active.payment_status} onChange={(e) => patch(active.id, { payment_status: e.target.value })} data-testid="booking-modal-payment">
                  {PAYMENT_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </label>
            </div>
            <p className="mt-4 text-xs text-slate-400">Marking payment as "paid" on a pending booking automatically confirms it and emails the client and expert.</p>
          </div>
        </div>
      )}
    </div>
  );
}
